<template>
  <div class="transaction-layout-default-block group">
    <div class="transaction-layout-default transaction-layout-full-date"
         :class="{'--w-time':displayTime,
                  'actions-enabled':displayActions}">
      <div>
        <Time class="md:w-auto"
              :class="{'opacity-50':transaction.status === 0}"
              :date="transaction.date"
              :date-format="displayDate"/>
      </div>
      <div class="flex items-center">
        <AccountIcon :icon="sourceAccount.icon || ''"
                     :icon-color="sourceAccount.icon_color"
                     :icon-bg="sourceAccount.icon_bg"
                     :class="{'opacity-50':transaction.status === 0}"/>
        <AccountName :name="sourceAccount.title"
                     :class="{'opacity-50':transaction.status === 0}" class="flex-1"/>
      </div>
      <div class="flex items-center">
        <div v-if="destinationAccount"
             class="flex items-center min-w-0 text-2sm text-text-secondary mr-4 flex-none max-w-xs"
             :class="{'opacity-50':transaction.status === 0}">
          <span class="transfer-destination-icon">
            <font-awesome-icon v-if="showTransferArrow"
                               icon="chevron-right"
                               class="transfer-arrow text-text-muted"/>
            <AccountIcon :icon="destinationAccount.icon || ''"
                         :icon-color="destinationAccount.icon_color"
                         :icon-bg="destinationAccount.icon_bg"/>
          </span>
          <span class="truncate">{{ destinationAccount.title }}</span>
        </div>
        <Category v-else-if="transactionCategory" :category="transactionCategory"
                  :with-icon="showCategoryIcon"
                  :icon-kind="categoryIconKind"
                  :class="{'opacity-50':transaction.status === 0}"
                  class="flex-none max-w-xs"/>
        <Comment v-if="transaction.comment || (!!transaction.transfer_id && !destinationAccount)"
                 :class="{'opacity-50':transaction.status === 0}"
                 :transaction="transaction"
                 :suppress-transfer="!!destinationAccount"
                 :comment="transaction.comment"/>
        <div class="ml-auto flex">
          <ExcludedInfo :off-report="transaction.off_report" :off-budget="transaction.off_budget"/>
          <Contractor v-if="!!transaction.contractor && displayContractor"
                      :class="{'opacity-50':transaction.status === 0}"
                      :contractor="transaction.contractor"/>
          <Tags v-if="!!transaction.tags.length"
                :class="{'opacity-50':transaction.status === 0}"
                :tags="transaction.tags"
                :type="type"/>
          <StatusPending v-if="transaction.status === 0"/>
        </div>
      </div>
      <div class="leading-relaxed justify-self-end flex items-center">
        <TransferReceivedHint v-if="transferSumsDiffer"
                              :sum="pairedTransfer.income.sum"
                              :currency="destinationAccount.currency"/>
        <Value :sum="shownSum"
               :sum-base="shownSumBase"
               :currency="sourceAccount.currency"
               :currency-display="currencyDisplay"
               :isExpense="shownIsExpense"
               :class="{'opacity-50':transaction.status === 0}"
               :type="type"/>
      </div>
      <div v-if="displayActions">
        <Actions @remove="$emit('remove')"
                 @edit="$emit('edit')"/>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import transactionCurrencyDisplayProp from "@/mixins/transactionCurrencyDisplayProp";
import Time from "@/components/common/Transaction/TransactionItem/Time";
import AccountIcon from "@/components/common/Transaction/TransactionItem/AccountIcon";
import AccountName from "@/components/common/Transaction/TransactionItem/AccountName";
import Category from "@/components/common/Transaction/TransactionItem/Category";
import Comment from "@/components/common/Transaction/TransactionItem/Comment";
import Contractor from "@/components/common/Transaction/TransactionItem/Contractor";
import Tags from "@/components/common/Transaction/TransactionItem/Tags";
import Value from "@/components/common/Transaction/TransactionItem/Value";
import Actions from "@/components/common/Transaction/TransactionItem/Actions";
import StatusPending from "@/components/common/Transaction/TransactionItem/StatusPending";
import ExcludedInfo from "@/components/common/Transaction/TransactionItem/ExcludedInfo";
import TransferReceivedHint from "@/components/common/Transaction/TransactionItem/TransferReceivedHint";

export default {
  name: "TransactionViewFullDate",
  components: {
    ExcludedInfo,
    TransferReceivedHint,
    StatusPending, AccountName, AccountIcon, Time, Category, Comment, Contractor, Tags, Value, Actions},
  mixins:[transactionCurrencyDisplayProp],
  props: {
    transaction: {
      type: Object,
      required: true
    },
    displayTime: {
      type: Boolean,
      required: true
    },
    displayContractor: {
      type: Boolean,
      required: true
    },
    markExpenseCategory: {
      type: Boolean,
      default: false
    },
    showTransferArrow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('space', ['currentUserIsWatcher']),
    type() {
      if (!!this.transaction.transfer_id) {
        return 'transfer'
      } else {
        return this.transaction.type === 1 ? 'income' : 'expense'
      }
    },
    transactionCategory() {
      if (this.transaction.type === 1) {
        return this.transaction.income;
      } else {
        return this.transaction.expense;
      }
    },
    showCategoryIcon() {
      return this.markExpenseCategory
        && (this.transaction.type === 1 || this.transaction.type === 2)
        && !this.transaction.transfer_id;
    },
    categoryIconKind() {
      return this.transaction.type === 1 ? 'income' : 'expense';
    },
    pairedTransfer() {
      if (!this.markExpenseCategory || !this.transaction.transfer_id) return null;
      const transfer = this.transaction.transfer;
      if (!transfer || !transfer.expense || !transfer.income) return null;
      if (!transfer.expense.account || !transfer.income.account) return null;
      return transfer;
    },
    sourceAccount() {
      return this.pairedTransfer ? this.pairedTransfer.expense.account : this.transaction.account;
    },
    destinationAccount() {
      return this.pairedTransfer ? this.pairedTransfer.income.account : null;
    },
    shownSum() {
      const sum = this.pairedTransfer ? this.pairedTransfer.expense.sum : this.transaction.sum;
      return Number(sum);
    },
    shownSumBase() {
      const sum = this.pairedTransfer ? this.pairedTransfer.expense.sum_base : this.transaction.sum_base;
      return sum == null ? sum : Number(sum);
    },
    shownIsExpense() {
      if (this.pairedTransfer) return true;
      return this.transaction.type === 2 || this.transaction.type === 4;
    },
    transferSumsDiffer() {
      if (!this.pairedTransfer) return false;
      const from = Number(this.pairedTransfer.expense.sum);
      const to = Number(this.pairedTransfer.income.sum);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
      return Math.round(from * 100) !== Math.round(to * 100);
    },
    displayDate() {
      return this.displayTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'
    },
    displayActions() {
      // Скрываем иконку редактирования для инвестиционных переводов (типы 3 и 4),
      // так как они редактируются через интерфейс инвестиций
      return !this.currentUserIsWatcher && this.transaction.type !== 3 && this.transaction.type !== 4
    }
  },
  i18n: {
    messages: {
      en: {
        transactionIsPending: 'Transaction is processing...'
      },
      ru: {
        transactionIsPending: 'Транзакция обрабатывается...'
      }
    }
  }
}
</script>

<style scoped lang="scss">
.transfer-destination-icon {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
}

.transfer-arrow {
  position: absolute;
  top: 50%;
  right: calc(100% + 6px);
  width: 9px;
  height: 9px;
  transform: translateY(-50%);
}

.transaction-layout-default-block {
  @apply relative;
  &:not(:last-of-type):after {
    @apply bg-border rounded w-full left-0 absolute rounded;
    content: '';
    height: 1px;
    bottom: 1px;
  }
}

.transaction-layout-default {
  @apply flex py-4 items-center col-gap-6 relative;
  > div {
    transition: all .2s;

    &:nth-of-type(1) {
      width: 6rem;
    }

    &:nth-of-type(2) {
      width: 22.4%;
      flex: none;
    }

    &:nth-of-type(3) {
      width: 0;
      flex: 1;
    }

    &:nth-of-type(4) {
      @apply -mr-6;
      width: auto;
    }

    &:nth-of-type(5) {
      width: 0;
      opacity: 0;
      overflow: hidden;
    }
  }

  &.--w-time {
    > div {
      &:nth-of-type(1) {
        width: 11rem;
      }
    }
  }

  &.actions-enabled {
    &:hover {
      > div:nth-of-type(4) {
        @apply -mr-4;
      }

      > div:nth-of-type(5) {
        width: 15px;
        opacity: 1;
      }
    }
  }
}

</style>